/**
 * CreateNewTheme - Editor de temas para crear y editar temas de color
 * Adaptado de NewLnF/components/CreateNewTheme.tsx para AdminConsole (JavaScript + MUI)
 * Versión simplificada para edición básica de temas
 */

import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Divider,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Palette as PaletteIcon,
} from "@mui/icons-material";
import { useAppRegistryStore } from "../../stores/appRegistryStore";
import "./CreateNewTheme.css";

// Función para convertir HSL a HEX (simplificada)
const hslToHex = (hsl) => {
  // Si ya es hex, retornarlo
  if (hsl.startsWith("#")) return hsl;
  
  // Extraer valores HSL
  const match = hsl.match(/hsl\((\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%\)/);
  if (!match) return hsl;
  
  const h = parseFloat(match[1]) / 360;
  const s = parseFloat(match[2]) / 100;
  const l = parseFloat(match[3]) / 100;
  
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
  const m = l - c / 2;
  
  let r = 0, g = 0, b = 0;
  
  if (h < 1/6) {
    r = c; g = x; b = 0;
  } else if (h < 2/6) {
    r = x; g = c; b = 0;
  } else if (h < 3/6) {
    r = 0; g = c; b = x;
  } else if (h < 4/6) {
    r = 0; g = x; b = c;
  } else if (h < 5/6) {
    r = x; g = 0; b = c;
  } else {
    r = c; g = 0; b = x;
  }
  
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);
  
  return `#${[r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }).join("")}`;
};

// Función para convertir HEX a HSL (simplificada)
const hexToHsl = (hex) => {
  if (!hex.startsWith("#")) return hex;
  
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  return `hsl(${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%)`;
};

const CreateNewTheme = ({ onClose, onSave, editingTheme }) => {
  const { settings } = useAppRegistryStore();
  const isDarkMode = settings?.currentTheme === "dark";
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  
  const [themeData, setThemeData] = useState({
    id: editingTheme?.id || `custom-theme-${Date.now()}`,
    name: editingTheme?.name || "Nuevo Tema",
    description: editingTheme?.description || "Un tema personalizado",
    family: editingTheme?.family || "custom",
    colors: editingTheme?.colors || {
      primary: "hsl(221.2 83.2% 53.3%)",
      secondary: "hsl(210 40% 96%)",
      accent: "hsl(210 40% 94%)",
      background: "hsl(0 0% 100%)",
      foreground: "hsl(222.2 84% 4.9%)",
      muted: "hsl(210 40% 96%)",
      mutedForeground: "hsl(215.4 16.3% 46.9%)",
      card: "hsl(0 0% 100%)",
      cardForeground: "hsl(222.2 84% 4.9%)",
      border: "hsl(214.3 31.8% 91.4%)",
      navbar: "hsl(0 0% 100%)",
      toolbar: "hsl(210 40% 98%)",
      header: "hsl(221.2 83.2% 53.3%)",
      paper: "hsl(0 0% 100%)",
      footer: "hsl(210 40% 96%)",
      destructive: "hsl(0 84.2% 60.2%)",
      destructiveForeground: "hsl(210 40% 98%)",
      ring: "hsl(221.2 83.2% 53.3%)",
      input: "hsl(214.3 31.8% 91.4%)",
      popover: "hsl(0 0% 100%)",
      popoverForeground: "hsl(222.2 84% 4.9%)",
    },
  });

  const colorFields = [
    { key: "primary", label: "Color Primario" },
    { key: "navbar", label: "Navbar" },
    { key: "header", label: "Header" },
    { key: "background", label: "Fondo" },
    { key: "foreground", label: "Texto Principal" },
    { key: "accent", label: "Acento" },
    { key: "border", label: "Bordes" },
    { key: "footer", label: "Footer" },
  ];

  const updateColor = (key, value) => {
    setThemeData((prev) => ({
      ...prev,
      colors: {
        ...prev.colors,
        [key]: value,
      },
    }));
  };

  const convertToFullTheme = (data) => {
    // Asegurar que todos los colores requeridos estén presentes
    const fullColors = {
      primary: data.colors.primary || "hsl(221.2 83.2% 53.3%)",
      secondary: data.colors.secondary || "hsl(210 40% 96%)",
      accent: data.colors.accent || "hsl(210 40% 94%)",
      background: data.colors.background || "hsl(0 0% 100%)",
      foreground: data.colors.foreground || data.colors.text || "hsl(222.2 84% 4.9%)",
      muted: data.colors.muted || "hsl(210 40% 96%)",
      mutedForeground: data.colors.mutedForeground || "hsl(215.4 16.3% 46.9%)",
      card: data.colors.card || data.colors.paper || "hsl(0 0% 100%)",
      cardForeground: data.colors.cardForeground || data.colors.foreground || data.colors.text || "hsl(222.2 84% 4.9%)",
      border: data.colors.border || "hsl(214.3 31.8% 91.4%)",
      navbar: data.colors.navbar || "hsl(0 0% 100%)",
      toolbar: data.colors.toolbar || "hsl(210 40% 98%)",
      header: data.colors.header || "hsl(221.2 83.2% 53.3%)",
      paper: data.colors.paper || data.colors.card || "hsl(0 0% 100%)",
      footer: data.colors.footer || "hsl(210 40% 96%)",
      destructive: data.colors.destructive || "hsl(0 84.2% 60.2%)",
      destructiveForeground: data.colors.destructiveForeground || "hsl(210 40% 98%)",
      ring: data.colors.ring || data.colors.primary || "hsl(221.2 83.2% 53.3%)",
      input: data.colors.input || data.colors.border || "hsl(214.3 31.8% 91.4%)",
      popover: data.colors.popover || data.colors.paper || "hsl(0 0% 100%)",
      popoverForeground: data.colors.popoverForeground || data.colors.foreground || data.colors.text || "hsl(222.2 84% 4.9%)",
    };

    return {
      id: data.id,
      name: data.name,
      description: data.description,
      family: data.family || "custom",
      colors: fullColors,
    };
  };

  const handleSave = () => {
    if (!themeData.name.trim()) {
      setSnackbar({
        open: true,
        message: "Por favor, ingresa un nombre para el tema",
        severity: "error",
      });
      return;
    }
    const fullTheme = convertToFullTheme(themeData);
    onSave(fullTheme);
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
          color: isDarkMode ? "#e0e0e0" : "#1a1a1a",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
          color: isDarkMode ? "#e0e0e0" : "#1a1a1a",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PaletteIcon sx={{ color: isDarkMode ? "#64b5f6" : "#1976d2" }} />
          <Typography variant="h5" sx={{ color: isDarkMode ? "#e0e0e0" : "#1a1a1a" }}>
            {editingTheme ? "Editar Tema" : "Crear Nuevo Tema"}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: isDarkMode ? "#e0e0e0" : "#1a1a1a" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
          color: isDarkMode ? "#e0e0e0" : "#1a1a1a",
        }}
      >
        <Stack spacing={3} sx={{ py: 2 }}>
          {/* Información básica */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: isDarkMode ? "#ffffff" : "#1a1a1a" }}>
              Información del Tema
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Nombre del Tema"
                value={themeData.name}
                onChange={(e) => setThemeData({ ...themeData, name: e.target.value })}
                fullWidth
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
                    color: isDarkMode ? "#e0e0e0" : "#1a1a1a",
                  },
                  "& .MuiInputLabel-root": {
                    color: isDarkMode ? "#b0b0b0" : "text.secondary",
                  },
                }}
              />
              <TextField
                label="Descripción"
                value={themeData.description}
                onChange={(e) => setThemeData({ ...themeData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
                    color: isDarkMode ? "#e0e0e0" : "#1a1a1a",
                  },
                  "& .MuiInputLabel-root": {
                    color: isDarkMode ? "#b0b0b0" : "text.secondary",
                  },
                }}
              />
            </Stack>
          </Box>

          <Divider sx={{ borderColor: isDarkMode ? "#404040" : "#e0e0e0" }} />

          {/* Colores principales */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: isDarkMode ? "#ffffff" : "#1a1a1a" }}>
              Colores del Tema
            </Typography>
            <Grid container spacing={2}>
              {colorFields.map((field) => {
                const colorValue = themeData.colors[field.key];
                const hexValue = hslToHex(colorValue);
                
                return (
                  <Grid item xs={12} sm={6} key={field.key}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          backgroundColor: colorValue,
                          border: `2px solid ${isDarkMode ? "#404040" : "#e0e0e0"}`,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          const input = document.createElement("input");
                          input.type = "color";
                          input.value = hexValue;
                          input.onchange = (e) => {
                            const newHex = e.target.value;
                            const newHsl = hexToHsl(newHex);
                            updateColor(field.key, newHsl);
                          };
                          input.click();
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: isDarkMode ? "#ffffff" : "#1a1a1a", mb: 0.5 }}>
                          {field.label}
                        </Typography>
                        <TextField
                          size="small"
                          value={colorValue}
                          onChange={(e) => updateColor(field.key, e.target.value)}
                          fullWidth
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
                              color: isDarkMode ? "#e0e0e0" : "#1a1a1a",
                              fontSize: "0.75rem",
                            },
                            "& .MuiInputLabel-root": {
                              color: isDarkMode ? "#b0b0b0" : "text.secondary",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
          borderTop: isDarkMode ? "1px solid #404040" : "1px solid #e0e0e0",
        }}
      >
        <Button
          onClick={onClose}
          sx={{ color: isDarkMode ? "#e0e0e0" : "#1a1a1a" }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
        >
          {editingTheme ? "Guardar Cambios" : "Crear Tema"}
        </Button>
      </DialogActions>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default CreateNewTheme;

